import { Injectable } from '@angular/core';
import { Observable, delay, of, throwError } from 'rxjs';
import { HttpResponseEntity } from '../../../../core/domain/common.models';
import { PinSettingData, PinValidationData } from '../../../../core/domain/pin-creation-domain.models';
import { OtpRequestData, OtpValidationData } from '../../../../core/domain/pin-otp-domain.models';
import { FSWalletPinTransactionRepository } from '../../../../core/repository/fs-wallet-pin-transaction.repository';

/**
 * Mock Implementation of Unified Wallet PIN Transaction Repository
 * Clean Architecture Data Layer - Test Infrastructure Implementation
 *
 * Provides comprehensive test scenarios for all PIN transaction operations:
 * - OTP request/validation with various error scenarios
 * - PIN validation and setting with business rule testing
 *
 * Test scenarios are based on input patterns for predictable testing
 */
@Injectable({ providedIn: 'root' })
export class MockFSWalletPinTransactionRepository extends FSWalletPinTransactionRepository {
    // ========== OTP OPERATIONS ==========

    /**
     * Mock OTP Request for PIN Transaction
     * Simulates various scenarios based on wallet_id patterns
     *
     * @param data - Domain data
     * @returns Observable of mock response
     */
    RequestWalletPinOtp(data: OtpRequestData): Observable<HttpResponseEntity> {
        console.log('🧪 Mock OTP Request:', data);

        // Simulate network delay
        const mockDelay = Math.random() * 1000 + 500; // 500-1500ms

        // Test scenarios based on wallet_id patterns
        if (data.wallet_id.includes('rate-limit')) {
            return throwError(() => ({
                error: {
                    status_code: 429,
                    message: 'Too many OTP request attempts. Please contact CS Paper.id.',
                    error_code: 'RATE_LIMIT_EXCEEDED',
                },
            })).pipe(delay(mockDelay));
        }

        if (data.wallet_id.includes('server-error')) {
            return throwError(() => ({
                error: {
                    status_code: 500,
                    message: 'Internal server error occurred',
                    error_code: 'INTERNAL_SERVER_ERROR',
                },
            })).pipe(delay(mockDelay));
        }

        if (data.wallet_id.includes('network-error')) {
            return throwError(() => ({
                error: {
                    status_code: 0,
                    message: 'Network connection failed',
                    error_code: 'NETWORK_ERROR',
                },
            })).pipe(delay(mockDelay));
        }

        if (data.wallet_id.includes('cooldown')) {
            return throwError(() => ({
                error: {
                    status_code: 400,
                    message: 'Failed to request new OTP, previous OTP not yet expired',
                    error_code: 'OTP_NOT_EXPIRED',
                },
            })).pipe(delay(mockDelay));
        }

        // Default success response
        const successResponse: HttpResponseEntity = {
            status_code: 200,
            message: 'OTP sent successfully',
            data: {
                request_id: `req_${Date.now()}`,
                expires_in: 300, // 5 minutes
            },
        };

        return of(successResponse).pipe(delay(mockDelay));
    }

    /**
     * Mock OTP Validation for PIN Transaction
     * Simulates various scenarios based on OTP code patterns
     *
     * @param data - Domain data
     * @returns Observable of mock response
     */
    ValidateWalletPinOtp(data: OtpValidationData): Observable<HttpResponseEntity> {
        console.log('🧪 Mock OTP Validation:', data);

        const otpCode = data.payload.otp;
        const mockDelay = Math.random() * 1000 + 500; // 500-1500ms

        // Test scenarios based on OTP code patterns
        switch (otpCode) {
            case '123456':
                // Success scenario
                return of({
                    status_code: 200,
                    message: 'OTP verified successfully',
                    data: {
                        id: `otp_${Date.now()}`,
                        client_ref_id: data.wallet_id,
                        user_ref_id: data.user_id,
                        next_action: 'pin_creation',
                        next_action_token: `token_${Date.now()}`,
                    },
                }).pipe(delay(mockDelay));

            case '000000':
                // Invalid OTP
                return throwError(() => ({
                    error: {
                        status_code: 400,
                        message: 'OTP Invalid',
                        error_code: 'INVALID_OTP',
                    },
                })).pipe(delay(mockDelay));

            case '111111':
                // Expired OTP
                return throwError(() => ({
                    error: {
                        status_code: 400,
                        message: 'OTP Expired, please resend verification code.',
                        error_code: 'OTP_EXPIRED',
                    },
                })).pipe(delay(mockDelay));

            case '222222':
                // Blocked (too many attempts)
                return throwError(() => ({
                    error: {
                        status_code: 429,
                        message:
                            'You have exceeded the OTP attempt limit. For security, you must wait up to 24 hours to try again',
                        error_code: 'OTP_BLOCKED',
                    },
                })).pipe(delay(mockDelay));

            case '333333':
                // Server error (500)
                return throwError(() => ({
                    error: {
                        status_code: 500,
                        message: 'Internal server error occurred',
                        error_code: 'INTERNAL_SERVER_ERROR',
                    },
                })).pipe(delay(mockDelay));

            case '444444':
                // Server error
                return throwError(() => ({
                    error: {
                        status_code: 500,
                        message: 'Internal server error occurred',
                        error_code: 'INTERNAL_SERVER_ERROR',
                    },
                })).pipe(delay(mockDelay));

            case '555555':
                // Network error
                return throwError(() => ({
                    error: {
                        status_code: 0,
                        message: 'Network connection failed',
                        error_code: 'NETWORK_ERROR',
                    },
                })).pipe(delay(mockDelay));

            case '999999':
                // Blocked alternative (same as 222222 but different code)
                return throwError(() => ({
                    error: {
                        status_code: 429,
                        message:
                            'You have exceeded the OTP attempt limit. For security, you must wait up to 24 hours to try again',
                        error_code: 'OTP_BLOCKED',
                    },
                })).pipe(delay(mockDelay));

            case '666666':
                // OTP mismatch
                return throwError(() => ({
                    error: {
                        status_code: 400,
                        message: 'OTP does not match',
                        error_code: 'OTP_MISMATCH',
                    },
                })).pipe(delay(mockDelay));

            default:
                // Default success for any other 6-digit code
                const mockResponse: HttpResponseEntity = {
                    status_code: 200,
                    message: 'OTP verified successfully',
                    data: {
                        id: `otp_${Date.now()}`,
                        client_ref_id: data.wallet_id,
                        user_ref_id: data.user_id,
                        next_action: 'pin_creation',
                        next_action_token: `token_${Date.now()}`,
                    },
                };
                return of(mockResponse).pipe(delay(mockDelay));
        }
    }

    // ========== PIN OPERATIONS ==========

    /**
     * Mock PIN Validation (Check PIN)
     * Simulates various scenarios based on PIN patterns
     *
     * @param data - Domain data for PIN validation
     * @returns Observable of mock response
     */
    CheckPin(data: PinValidationData): Observable<HttpResponseEntity> {
        console.log('🧪 Mock PIN Validation:', data);

        const pin = data.pin;
        const mockDelay = 1000; // 1 second delay for better loading indicator visibility

        // Test scenarios based on PIN patterns
        switch (pin) {
            case '123456':
                // Valid PIN (success)
                return of({
                    status_code: 200,
                    message: 'PIN validation successful',
                }).pipe(delay(mockDelay));

            case '111111':
                // Invalid PIN (repeated digits)
                return throwError(() => ({
                    error: {
                        status_code: 400,
                        message:
                            'PIN does not meet criteria. PIN should not be 6 consecutive numbers or 6 same numbers.',
                        error_code: 'PIN_NOT_MEET_CRITERIA',
                    },
                })).pipe(delay(mockDelay));

            case '234567':
                // Invalid PIN (sequential digits)
                return throwError(() => ({
                    error: {
                        status_code: 400,
                        message:
                            'PIN does not meet criteria. PIN should not be 6 consecutive numbers or 6 same numbers.',
                        error_code: 'PIN_NOT_MEET_CRITERIA',
                    },
                })).pipe(delay(mockDelay));

            case '999999':
                // PIN already exists
                return throwError(() => ({
                    error: {
                        status_code: 400,
                        message: 'New PIN must be different from the existing one.',
                        error_code: 'PIN_ALREADY_EXISTS',
                    },
                })).pipe(delay(mockDelay));

            case '777777':
                // Server error
                return throwError(() => ({
                    error: {
                        status_code: 500,
                        message: 'A server error occurred. Please try again later.',
                        error_code: 'INTERNAL_SERVER_ERROR',
                    },
                })).pipe(delay(mockDelay));

            case '888888':
                // Validation failed
                return throwError(() => ({
                    error: {
                        status_code: 422,
                        message: 'There is a problem with your request. Please check again.',
                        error_code: 'VALIDATION_FAILED',
                    },
                })).pipe(delay(mockDelay));

            default:
                // Default success for any other 6-digit PIN
                return of({
                    status_code: 200,
                    message: 'PIN validation successful',
                }).pipe(delay(mockDelay));
        }
    }

    /**
     * Mock PIN Setting (Set PIN)
     * Simulates various scenarios based on PIN patterns
     *
     * @param data - Domain data for PIN setting
     * @returns Observable of mock response
     */
    SetPin(data: PinSettingData): Observable<HttpResponseEntity> {
        console.log('🧪 Mock PIN Setting:', data);

        const pin = data.pin;
        const mockDelay = 1000; // 1 second delay for better loading indicator visibility

        // Test scenarios based on PIN patterns
        switch (pin) {
            case '999999':
                // PIN already exists (for setting operation)
                return throwError(() => ({
                    error: {
                        status_code: 400,
                        message: 'New PIN must be different from the existing one.',
                        error_code: 'PIN_ALREADY_EXISTS',
                    },
                })).pipe(delay(mockDelay));

            case '777777':
                // Server error
                return throwError(() => ({
                    error: {
                        status_code: 500,
                        message: 'A server error occurred. Please try again later.',
                        error_code: 'INTERNAL_SERVER_ERROR',
                    },
                })).pipe(delay(mockDelay));

            case '666666':
                // Network error
                return throwError(() => ({
                    error: {
                        status_code: 0,
                        message: 'Network connection failed',
                        error_code: 'NETWORK_ERROR',
                    },
                })).pipe(delay(mockDelay));

            default:
                // Default success for any other 6-digit PIN
                return of({
                    status_code: 200,
                    message: 'PIN set successfully',
                    data: {
                        pin_id: `pin_${Date.now()}`,
                        wallet_id: data.wallet_id,
                        created_at: new Date().toISOString(),
                    },
                }).pipe(delay(mockDelay));
        }
    }
}
